import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";
import { CasinoCard } from "@/components/CasinoCard";
import { RiskNotice } from "@/components/RiskNotice";
import { FAQ, buildFAQSchema } from "@/components/FAQ";
import { listCasinos } from "@/data/casinos";

export function CasinoUtanSpelpausPage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const casinos = listCasinos({ noSpelpaus: true }).sort(
    (a, b) =>
      Number(b.priority_brand) - Number(a.priority_brand) ||
      b.overall_rating - a.overall_rating,
  );

  const title = isSv ? "Casino utan Spelpaus 2026 — vad du behöver veta" : "Casinos without Spelpaus 2026 — what you need to know";
  const desc = isSv
    ? "Casinon utan Spelpaus är inte anslutna till det svenska självavstängningsregistret. Den här sidan finns för information — inte för att kringgå skydd."
    : "Casinos without Spelpaus are not connected to the Swedish self-exclusion register. This page is informational — not a way to bypass safeguards.";

  const faq = [
    {
      question_sv: "Vad är Spelpaus?",
      question_en: "What is Spelpaus?",
      answer_sv:
        "Spelpaus.se är det nationella avstängningsregistret för spelbolag med svensk licens. Du loggar in med BankID och stänger av dig från allt licensierat spel under 1, 3, 6 eller 12 månader.",
      answer_en:
        "Spelpaus.se is the national self-exclusion register for Swedish-licensed operators. You log in with BankID and self-exclude from all licensed gambling for 1, 3, 6 or 12 months.",
    },
    {
      question_sv: "Är casinon utan Spelpaus säkrare än svenska?",
      question_en: "Are casinos without Spelpaus safer than Swedish ones?",
      answer_sv:
        "Nej. Casinon utanför svensk licens har ofta lägre konsumentskydd, eventuell skatteplikt på vinster och saknar svensk konsumenträtt. Spelpaus är ett verktyg för att skydda dig själv — inte ett tecken på dålig kvalitet hos svenska casinon.",
      answer_en:
        "No. Casinos outside the Swedish license often have lower consumer protection, possible tax on winnings, and no Swedish consumer law. Spelpaus is a tool to protect yourself — not a sign of low quality on Swedish sites.",
    },
    {
      question_sv: "Vad gör jag om jag har spelproblem?",
      question_en: "What if I have gambling problems?",
      answer_sv:
        "Kontakta Stödlinjen 020-81 91 00 eller besök stodlinjen.se. Använd spelpaus.se för att stänga av dig från alla svenska licensierade casinon med en knapptryckning.",
      answer_en:
        "Contact Stödlinjen at 020-81 91 00 or visit stodlinjen.se. Use spelpaus.se to self-exclude from all Swedish-licensed casinos with a single click.",
    },
    {
      question_sv: "Kan jag avbryta Spelpaus i förtid?",
      question_en: "Can I lift Spelpaus early?",
      answer_sv:
        "Nej. När en avstängning är aktiverad kan den inte hävas i förtid. Du måste vänta tills perioden löper ut.",
      answer_en:
        "No. Once a self-exclusion is active it cannot be lifted early. You must wait for the period to expire.",
    },
  ];

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} jsonLd={buildFAQSchema(faq, lang)} />
      <Breadcrumbs items={[{ label: t("nav.home"), href: prefix }, { label: title }]} />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{desc}</p>

      <div className="mt-4">
        <RiskNotice variant="spelpaus" />
      </div>

      <section className="mt-8 prose prose-ink max-w-none">
        <h2>{isSv ? "Hur Spelpaus fungerar" : "How Spelpaus works"}</h2>
        <p>
          {isSv
            ? "Spelpaus.se administreras av Spelinspektionen och är obligatoriskt för alla operatörer med svensk spellicens. Du loggar in med BankID, väljer en avstängningsperiod på 1, 3, 6 eller 12 månader, och inom sekunder kan ingen svenskt licensierad sajt erbjuda dig spel, marknadsföring eller bonus."
            : "Spelpaus.se is administered by Spelinspektionen and mandatory for every operator with a Swedish license. You log in with BankID, choose a period of 1, 3, 6 or 12 months, and within seconds no Swedish-licensed site can offer you gambling, marketing or bonuses."}
        </p>
        <h3>{isSv ? "Varför vissa casinon inte är anslutna" : "Why some casinos are not connected"}</h3>
        <p>
          {isSv
            ? "Casinon med utländsk licens — exempelvis MGA, Curaçao eller estnisk — är inte skyldiga att kontrollera Spelpaus. Det innebär att en spelare som stängt av sig själv via Spelpaus tekniskt sett kan registrera konto hos en utländsk sajt. Det är ett medvetet beslut från lagstiftaren att Spelpaus bara gäller den svenska marknaden."
            : "Casinos with foreign licenses — for example MGA, Curaçao or Estonian — are not required to check Spelpaus. That means a player self-excluded via Spelpaus can technically register at a foreign site. It is a deliberate legislative choice that Spelpaus only covers the Swedish market."}
        </p>
        <h3>{isSv ? "Vårt redaktionella ställningstagande" : "Our editorial stance"}</h3>
        <p>
          {isSv
            ? "Den här sidan finns för att informera. Vi rekommenderar inte att en spelare med risk för spelberoende söker upp casinon utan Spelpaus. Om du eller någon nära dig kämpar med spelandet är Stödlinjen 020-81 91 00 det första numret att ringa."
            : "This page exists to inform. We do not recommend that a player at risk of problem gambling seek out casinos without Spelpaus. If you or someone close to you is struggling with gambling, Stödlinjen at 020-81 91 00 is the first number to call."}
        </p>
      </section>

      <h2 className="mt-10 text-2xl font-bold">
        {isSv ? "Lista över casinon utan Spelpaus" : "List of casinos without Spelpaus"}
      </h2>
      <div className="mt-4 grid gap-4">
        {casinos.map((c, i) => (
          <CasinoCard key={c.id} casino={c} rank={i + 1} source="utanspelpaus-list" />
        ))}
      </div>

      <div className="mt-10">
        <FAQ items={faq} />
      </div>

      <InternalLinks context="spelpaus" />
    </div>
  );
}
